import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";
import {
  DuplicateJournalResponseDTO,
  JournalCountsResponseDTO,
  JournalQueryParamsDTO,
  JournalsResponseDTO,
} from "../models/JournalDTOs";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

const getAuthHeaders = () => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData,
    );
  }

  return response.json();
};

const appendParam = (
  queryParams: URLSearchParams,
  key: string,
  value: string | number | undefined,
) => {
  if (value !== undefined && value !== "") {
    queryParams.append(key, value.toString());
  }
};

export const fetchJournals = async (
  params: JournalQueryParamsDTO = {},
): Promise<JournalsResponseDTO> => {
  const queryParams = new URLSearchParams();
  appendParam(queryParams, "limit", params.limit);
  appendParam(queryParams, "offset", params.offset);
  appendParam(queryParams, "status", params.status);
  appendParam(queryParams, "search", params.search);
  appendParam(queryParams, "date_from", params.date_from);
  appendParam(queryParams, "date_to", params.date_to);
  appendParam(queryParams, "sort_by", params.sort_by);
  appendParam(queryParams, "sort_order", params.sort_order);

  const url = `${API_BASE_URL}/api/journals${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await parseJsonResponse<JournalsResponseDTO>(response);
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const fetchJournalCounts = async (params: {
  date_from?: string;
  date_to?: string;
} = {}): Promise<JournalCountsResponseDTO> => {
  const queryParams = new URLSearchParams();
  appendParam(queryParams, "date_from", params.date_from);
  appendParam(queryParams, "date_to", params.date_to);

  const url = `${API_BASE_URL}/api/journals/counts${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return await parseJsonResponse<JournalCountsResponseDTO>(response);
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const duplicateJournal = async (
  journalId: number,
): Promise<DuplicateJournalResponseDTO> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/journals/${journalId}/duplicate`,
      {
        method: "POST",
        headers: getAuthHeaders(),
      },
    );

    return await parseJsonResponse<DuplicateJournalResponseDTO>(response);
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};
